import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { Resp, useApi } from '@paradigm/api';
import SubmittedProvider from '@paradigm/features/src/forms/submitted-context';
import { getUserTicker } from '@paradigm/store/auth';
import createSafeContext from '@paradigm/utils/createSafeContext';

import reducer, {
  toggleSide,
  changeAccount,
  changeIsPostOnly,
  changeQuantity,
  changeQuoteTimer,
  changePrice,
  INITIAL_STATE,
  emptyTwoWay,
  changeLegPrice,
  switchLegPrices,
  ChangeLegPriceArgs,
  QuoteBuilderState,
} from '#/unified-rfqs/ui/grfq/quote-builder/form-reducer';
import { BboState, useGrfqBbo } from '#/unified-rfqs/ui/rfqs/bbo';
import { getOppositeSide } from '#/unified-rfqs/ui/rfqs/quotes-functions';
import { useGrfqRfq } from '#/unified-rfqs/ui/rfq-context';
import {
  useGrfqRfqQuotes,
  useGrfqService,
} from '#/unified-rfqs/contexts/grfqs';
import { Grfq, GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';

import { getProduct } from '#/products/domain';
import { PricingReq } from '#/unified-rfqs/repositories/grfq-types';
import { Side } from '#/unified-rfqs/entities/shared';
import { createView, ViewCtx } from '#/unified-rfqs/ui/grfq/quote-builder/view';
import { QuoteBuilderSubmitProvider } from '#/unified-rfqs/ui/grfq/quote-builder/submit-context';
import { useQuoteBuilderModalState } from '#/unified-rfqs/ui/grfq/quote-builder/modal-context';
import {
  useIsMultiLegStrategy,
  usePricingAlgoVersion,
} from '#/unified-rfqs/ui/hooks/shared';
import { useDefaultAccountsActions } from '#/preferences/default-accounts/context';

type ErrorCallback = (message: string) => void;

interface LoadLegPricesArgs {
  readonly price: string;
  readonly side: Side;
  readonly onError: ErrorCallback;
}

interface QuoteBuilderActions {
  changeAccount: (account: string) => void;
  changeIsPostOnly: (isPostOnly: boolean) => void;
  changeQuoteTimer: (quoteTimer: string) => void;
  changeQuantity: (quantity: string) => void;
  changePrice: (price: string) => void;
  changeLegPrice: (legIndex: ChangeLegPriceArgs) => void;
  fillLegPricesFromSide: (legs: GrfqQuote['legs']) => void;
  loadLegPrices: (args: LoadLegPricesArgs) => void;
  loadLegPricesFromQuote: (args: LoadLegPricesArgs) => void;
  changeLegPricesFromBbo: (side: Side, isEmptyValueAllowed?: boolean) => void;
  toggleSide: () => void;
}

export const [useQuoteBuilderActions, ActionsCtxProvider] =
  createSafeContext<QuoteBuilderActions>('QuoteBuilderActions');

interface Props {
  children: ReactNode;
}

export function QuoteBuilderProvider({ children }: Props) {
  const bboResp = useGrfqBbo();
  const rfq = useGrfqRfq();
  const quotes = useGrfqRfqQuotes(rfq.id);
  const legPricesFromQuotes = useLegPricesFromQuotes(rfq.id);
  const myDesk = useSelector(getUserTicker)!;
  const { legPricingEnabled, clickedQuote } = useQuoteBuilderModalState();
  const { legs, product_codes } = rfq;
  const filteredLegs = legs.filter((leg) => !leg.isHedge);
  const isSingleLeg = filteredLegs.length === 1;
  const haveOptions = product_codes.some(
    (code) => getProduct(code).kind === 'OPTION',
  );

  const productCode = product_codes[0];
  const [state, dispatch] = useQuoteBuilderReducer();
  const { side: modalSide } = state;
  const [isFetchingLegPrices, fetchLegPrices] = useLegPricesFetcher(rfq);

  const actions: QuoteBuilderActions = useMemo(
    () => ({
      toggleSide: () => {
        dispatch(toggleSide());
        // Should keep previous price when switching side
        dispatch(switchLegPrices());
      },
      changeAccount: (account) => dispatch(changeAccount(account)),
      changeIsPostOnly: (isPostOnly) => dispatch(changeIsPostOnly(isPostOnly)),
      changeQuoteTimer: (quoteTimer) => dispatch(changeQuoteTimer(quoteTimer)),
      changeQuantity: (quantity) => dispatch(changeQuantity(quantity)),
      changePrice: (price) => dispatch(changePrice(price)),
      changeLegPrice: (indexLeg) => dispatch(changeLegPrice(indexLeg)),
      fillLegPricesFromSide: (sideLegs) => {
        sideLegs.forEach((leg, index) => {
          const isSell = modalSide === 'SELL';
          const side = isSell ? getOppositeSide(leg.side) : leg.side;
          actions.changeLegPrice({ index, side, value: leg.price ?? '' });
        });
      },
      loadLegPrices: ({ price, side, onError }) => {
        const onSuccess = (legPrices: GrfqQuote['legs']) => {
          actions.fillLegPricesFromSide(legPrices);
          actions.changePrice(price);
        };

        fetchLegPrices(side, price, onSuccess, onError);
      },
      loadLegPricesFromQuote: ({ price, onError }) => {
        const legPrices = legPricesFromQuotes[price];

        if (legPrices == null) {
          onError(
            'Failed to load leg prices from quote, please enter leg prices manually.',
          );
          return;
        }
        actions.fillLegPricesFromSide(legPrices);
        actions.changePrice(price);
      },
      changeLegPricesFromBbo: (bboSide, isEmptyValueAllowed = true) => {
        if (bboResp.data == null) return;
        const { best_bid, best_ask, leg_prices } = bboResp.data;

        if (bboSide === 'BUY' && best_bid.quantity === '0') return;
        if (bboSide === 'SELL' && best_ask.quantity === '0') return;
        if (leg_prices.length !== rfq.legs.filter((leg) => !leg.isHedge).length)
          return;

        const indexLegs = leg_prices.map((leg, index) => {
          const { best_bid_price, best_ask_price, best_ask_iv, best_bid_iv } =
            leg;
          let legSide = rfq.legs[index]!.side;

          const isSellBboSide = bboSide === 'SELL';
          const isSellModalSide = modalSide === 'SELL';

          let value = legSide === 'BUY' ? best_bid_price : best_ask_price;
          const iv = legSide === 'BUY' ? best_bid_iv : best_ask_iv;
          if (isSellBboSide) {
            value = legSide === 'BUY' ? best_ask_price : best_bid_price;
          }

          if (isSellModalSide) {
            legSide = getOppositeSide(legSide);
          }

          return { index, side: legSide, value, iv };
        });

        const hasEmptyValue = (leg: ChangeLegPriceArgs) =>
          leg.value === '' || Number(leg.value) === 0;

        if (indexLegs.some(hasEmptyValue) && !isEmptyValueAllowed) return;

        indexLegs.forEach(actions.changeLegPrice);
      },
    }),
    [
      dispatch,
      modalSide,
      fetchLegPrices,
      legPricesFromQuotes,
      bboResp.data,
      rfq.legs,
    ],
  );

  const isPrefillingQuotes = usePrefillQuotes(
    rfq,
    actions,
    bboResp,
    state,
    clickedQuote,
  );

  const view = useMemo(
    () =>
      createView({
        state,
        legs,
        bbo: bboResp.data,
        legPricingEnabled,
        quotes,
        myDesk,
        isPrefillingQuotes,
        isLegPricesLoading: isFetchingLegPrices,
        productCode,
        isSingleLeg,
        haveOptions,
      }),
    [
      state,
      legs,
      bboResp.data,
      legPricingEnabled,
      quotes,
      myDesk,
      isPrefillingQuotes,
      isFetchingLegPrices,
      productCode,
      isSingleLeg,
      haveOptions,
    ],
  );

  return (
    <SubmittedProvider>
      <ViewCtx.Provider value={view}>
        <ActionsCtxProvider value={actions}>
          <QuoteBuilderSubmitProvider>{children}</QuoteBuilderSubmitProvider>
        </ActionsCtxProvider>
      </ViewCtx.Provider>
    </SubmittedProvider>
  );
}

type PrefillState = 'loading' | 'prefilling' | 'filled';

/**
 * Automatically prefill quote field, using the following criteria:
 * - If user clicked on an specific quote: prefill with quote values
 * - If user clicked on an empty quote:
 *   - If user has a quote of his own: prefill price with user quote
 *   - else: prefill price with screen best bid/ask
 */
function usePrefillQuotes(
  rfq: Grfq,
  actions: QuoteBuilderActions,
  bboState: BboState,
  state: QuoteBuilderState,
  clickedQuote: GrfqQuote | null,
) {
  const hasPrefilledRef = useRef(false);
  const [prefillState, setPrefillState] = useState<PrefillState>('loading');

  const [ownQuoteState, fetchOwnSideQuote] = useLastOwnQuoteApi();
  const isMultiLegStrategy = useIsMultiLegStrategy(rfq);

  const { isLoading: isLoadingOwnQuote, resp: ownQuoteResp } = ownQuoteState;
  const { isLoading: isLoadingBbo, data: bboData } = bboState;
  const { side: modalSide, price: modalPrice } = state;

  useEffect(() => {
    // Prefill values only on quote builder open
    if (hasPrefilledRef.current) return;

    fetchOwnSideQuote({ rfqId: rfq.id, side: modalSide });
  }, [modalSide, fetchOwnSideQuote, prefillState, rfq.id]);

  useEffect(() => {
    if (isLoadingOwnQuote || isLoadingBbo) return;

    setPrefillState('prefilling');
  }, [isLoadingOwnQuote, isLoadingBbo]);

  const prefillPricesFromBbo = useCallback(() => {
    if (bboData == null) return;

    if (isMultiLegStrategy) {
      actions.changeLegPricesFromBbo(modalSide, false);
    }

    const bestSideKey = modalSide === 'BUY' ? `best_bid` : `best_ask`;
    const { [bestSideKey]: bestSideQuote } = bboData;

    const quantity = Number(bestSideQuote.quantity);
    const price = Number(bestSideQuote.price);
    if (quantity !== 0 || price !== 0) {
      actions.changePrice(bestSideQuote.price);
      return;
    }
    actions.changePrice(bboData.mark_price);
  }, [bboData, isMultiLegStrategy, modalSide, actions]);

  useEffect(() => {
    if (
      ['loading', 'filled'].includes(prefillState) ||
      hasPrefilledRef.current ||
      ownQuoteResp == null
    )
      return;

    hasPrefilledRef.current = true;

    if (clickedQuote != null) {
      if (isMultiLegStrategy) {
        actions.fillLegPricesFromSide(clickedQuote.legs);
      } else {
        actions.changePrice(clickedQuote.price);
      }

      setPrefillState('filled');
      return;
    }

    const now = Date.now();
    const [quantity, price, legs] = getPrefillQuoteValues(now, ownQuoteResp);

    const hasOwnQuote = price !== '';
    if (hasOwnQuote) {
      actions.changeQuantity(quantity);

      if (isMultiLegStrategy) {
        actions.fillLegPricesFromSide(legs);
      } else {
        actions.changePrice(price);
      }
    } else if (modalPrice === '') {
      prefillPricesFromBbo();
    }

    setPrefillState('filled');
  }, [
    ownQuoteResp,
    actions,
    prefillState,
    modalSide,
    clickedQuote,
    prefillPricesFromBbo,
    isMultiLegStrategy,
    modalPrice,
  ]);

  return prefillState !== 'filled';
}

const OWN_QUOTE_INITIAL_STATE = { isLoading: true, resp: null } as const;

function useLastOwnQuoteApi() {
  const { getLastOwnSideQuote } = useGrfqService();
  return useApi(getLastOwnSideQuote, OWN_QUOTE_INITIAL_STATE);
}

/**
 * Get values to be pre-filled on the quote builder form
 * @param now Current time in milliseconds unix time
 * @param resp The response of the "latest quote" API call
 * @returns Tuple of quantity and price respectively
 */
function getPrefillQuoteValues(
  now: number,
  resp: Resp<GrfqQuote | undefined>,
): [quantity: string, price: string, legs: GrfqQuote['legs']] {
  if (
    !resp.ok ||
    resp.data == null ||
    isOlderThan24h(now, resp.data.created) ||
    resp.data.status === 'CLOSED'
  )
    return ['', '', []];
  const quote = resp.data;
  const useOriginalQty =
    quote.status === 'CLOSED' &&
    Number.parseFloat(quote.remaining_quantity) === 0;
  const qty = useOriginalQty
    ? quote.original_quantity
    : quote.remaining_quantity;
  return [qty, quote.price, quote.legs];
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function isOlderThan24h(now: number, date: Date) {
  return now - date.getTime() > DAY_IN_MS;
}

function useLegPricesFetcher(rfq: Grfq) {
  const { getLegPricing } = useGrfqService();
  const [{ isLoading }, fetchLegPrices] = useApi(getLegPricing);
  const pricingAlgoVersion = usePricingAlgoVersion(rfq);
  const { legs, venue } = rfq;

  const fetcher = useCallback(
    (
      side: Side,
      price: string,
      onSuccess: QuoteBuilderActions['fillLegPricesFromSide'],
      onError: ErrorCallback,
    ) => {
      const sidedPriceKey = side === 'BUY' ? 'ask_price' : 'bid_price';

      const req: PricingReq = {
        legs: legs.filter((leg) => !leg.isHedge),
        venue,
        [sidedPriceKey]: price,
        algorithm_version: pricingAlgoVersion,
      };

      fetchLegPrices(req, (resp) => {
        if (!resp.ok) {
          const message = resp.message.trim().replace(/\.+$/u, '');
          onError(`${message}. Please enter leg prices manually.`);
          return;
        }
        const prices = resp.data[`${sidedPriceKey}s`];

        const sideLegs = prices.map((value, index) => ({
          side: legs[index]!.side,
          price: value,
          isHedge: false,
        }));

        onSuccess(sideLegs);
      });
    },
    [fetchLegPrices, pricingAlgoVersion, legs, venue],
  );

  return [isLoading, fetcher] as const;
}

function useLegPricesFromQuotes(rfqId: Grfq['id']) {
  const { bids, asks } = useGrfqRfqQuotes(rfqId);

  return useMemo(
    () =>
      Object.fromEntries(
        [...asks, ...bids].map((quote) => [quote.price, quote.legs]),
      ),
    [asks, bids],
  );
}

function useQuoteBuilderReducer() {
  const { venue, legs, product_codes } = useGrfqRfq();
  const { getDefaultAccount } = useDefaultAccountsActions();
  const { clickSide, quantity = '', price = '' } = useQuoteBuilderModalState();
  const productCode = product_codes[0];
  const initialState: QuoteBuilderState = useMemo(
    () => ({
      ...INITIAL_STATE,
      account: getDefaultAccount(venue, productCode) ?? undefined,
      legsPrice: legs.filter((leg) => !leg.isHedge).map(() => emptyTwoWay),
      side: clickSide,
      quantity,
      price,
    }),
    [clickSide, getDefaultAccount, legs, price, productCode, quantity, venue],
  );

  return useReducer(reducer, initialState);
}
